/**
 * Utilidades puras, sin dependencia del framework.
 *
 * Vivían en src/services/core/Utils.ts, junto a la clase Service que envolvía
 * Vue.observable(). Ese directorio desaparece al migrar los servicios a Pinia,
 * y estas dos funciones no tienen nada que ver con estado reactivo.
 */

/** Una función que produce un efecto y no devuelve nada. */
export type Procedure = (...args: any[]) => void;

export type DebounceOptions = {
  /** Ejecuta en la primera llamada en vez de al final de la ventana. */
  isImmediate: boolean;
};

/**
 * Ejecuta una función y descarta las llamadas siguientes durante un tiempo.
 * Se usa en los autocompletados (búsqueda de usuarios, de llaves) con 300 ms.
 */
export function debounce<F extends Procedure>(
  func: F,
  waitMilliseconds = 50,
  options: DebounceOptions = { isImmediate: false }
): (this: ThisParameterType<F>, ...args: Parameters<F>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  return function (this: ThisParameterType<F>, ...args: Parameters<F>) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;

    const doLater = function () {
      timeoutId = undefined;
      if (!options.isImmediate) {
        func.apply(context, args);
      }
    };

    const shouldCallNow = options.isImmediate && timeoutId === undefined;

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(doLater, waitMilliseconds);

    if (shouldCallNow) {
      func.apply(context, args);
    }
  };
}

/**
 * Compara dos objetos por sus valores de primer nivel.
 *
 * Lo usan las vistas Index para decidir si un evento de paginación cambió algo
 * de verdad antes de disparar otra consulta a la API.
 */
export function shallowCompare<T>(
  val1: Record<string, T>,
  val2: Record<string, T>
): boolean {
  const keys1 = Object.keys(val1);
  const keys2 = Object.keys(val2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (val1[key] !== val2[key]) {
      return false;
    }
  }

  return true;
}
